export const renderBrowserTestingWorkflowDetails =
  (): string => `### Playbook step authoring guidance

- Use **semantic targets** from the accessibility tree (button text, field labels, placeholder text) — NOT refs or CSS selectors
- Target format: describe as a human would ("Submit button", "Email input field", "Status dropdown")
- For \`fill\` actions: include \`params: { "value": "..." }\`
- **Always prefer macros over hardcoded test data.** Macros generate fresh values each run, preventing flaky tests from stale or colliding data. Never hardcode emails, names, phone numbers, or dates when a macro can generate them.
- Prefix test data with "QA Test" for identifiability (e.g., \`"QA Test $FIRST_NAME()"\`)
- Valid actions: \`click\`, \`fill\`, \`fill_form\`, \`select\`, \`navigate\`, \`wait\`, \`scroll\`, \`hover\`, \`upload\`, \`verify\`, \`press_key\`, \`drag\`, \`resize\`, \`capture\`, \`extract_table\`, \`python_execute\`, \`verify_download\`, \`verify_download_content\`, \`screenshot\`, \`agent_loop\`

### Using macros and variables

Macros are resolved at run time — each execution gets fresh values, making tests repeatable and collision-free.

**Available macros:**

| Macro | Args | Output | Example |
|-------|------|--------|---------|
| \`$EMAIL()\` | — | Unique test email | \`test-xk9mq2@example-test.com\` |
| \`$FIRST_NAME()\` | — | Random first name | \`Jordan\` |
| \`$LAST_NAME()\` | — | Random last name | \`Chen\` |
| \`$PHONE()\` | — | US phone (555 prefix) | \`+1-555-382-7491\` |
| \`$UUID()\` | — | Random UUID v4 | \`a1b2c3d4-...\` |
| \`$RANDOM_INT(min, max)\` | 2 ints | Random integer (inclusive) | \`$RANDOM_INT(100,999)\` → \`427\` |
| \`$RANDOM_STRING(length)\` | 1 int (1–1000) | Alphanumeric string | \`$RANDOM_STRING(8)\` → \`xK9mQ3bR\` |
| \`$TODAY()\` | — | Current date (YYYY-MM-DD) | \`2026-04-06\` |
| \`$DATE(days)\` | 1 int | Date offset from today | \`$DATE(-7)\` → last week |
| \`$NOW()\` | — | ISO 8601 timestamp | \`2026-04-06T14:30:00.000Z\` |
| \`$TIMESTAMP()\` | — | Unix milliseconds | \`1771520400000\` |
| \`$SEQUENCE("name")\` | counter name | Auto-incrementing per run | \`1\`, \`2\`, \`3\`... |

**When to use macros:**
- Email fields → \`$EMAIL()\`
- Name fields → \`$FIRST_NAME()\` / \`$LAST_NAME()\`
- Amounts, quantities, identifiers → \`$RANDOM_INT()\` or \`$RANDOM_STRING()\`
- Date fields → \`$TODAY()\` / \`$DATE()\`
- Sequential numbering → \`$SEQUENCE("name")\`
- Macros combine inline: \`"QA Test $FIRST_NAME() $LAST_NAME()"\`, \`"order-$RANDOM_STRING(6)"\`

**Variables (\`$var.name\`):**
- Steps can reference data captured from earlier steps using \`$var.name\`
- Variable values are extracted during execution and available to subsequent steps
- Use step-qualified refs when multiple steps set the same variable: \`$var.Step2_Create_Client.name\`

### Credential selection

**Always use \`credentialId\` (UUID) in the login node — never just \`credentialName\`.**

1. **List available credentials** before authoring:
   \`\`\`bash
   canary credential list --json
   \`\`\`
   This returns each credential's \`id\`, \`name\`, \`isDefault\`, \`propertyName\`, and environment info.

2. **Pick the right credential**:
   - Prefer the **default credential** (\`isDefault: true\`)
   - **Never** use a credential with "local" in the name — it won't work in deployed environments
   - If multiple non-local credentials exist and you're unsure which to use, **ask the user**

3. **Set both \`credentialId\` and \`credentialName\`** in the login node's \`configJson\`:
   \`\`\`json
   { "credentialId": "4f6db838-...", "credentialName": "QA User" }
   \`\`\`

4. **If no matching credential exists**, warn the user that one needs to be created first in the web app.

The CLI will auto-resolve credentials if \`credentialId\` is missing, but it may fail if the choice is ambiguous. Always specify the ID explicitly for reliable uploads.

### Approving workflows and suite eligibility

New workflows start as \`draft\`. A test runs in suite regressions once it is
**eligible** — derived automatically, on two inputs:

- **Approve** (\`canary workflow approve <id> --yes\`) — the approval axis: a human verified the test checks the right thing (\`draft\` → \`approved\`).
- **Pass at least once** — run the test (\`canary workflow run <id>\`) and let it succeed. Once a flow is approved AND has passed, it is eligible for suite runs; there is no separate promote step.
- **Pause / unpause** (\`canary workflow pause <id>\` / \`canary workflow unpause <id>\`) — a standalone toggle that excludes an otherwise-eligible flow from suite runs, and re-includes it.
- Reliability is tracked automatically by the workflow's \`health\` (starts \`unverified\`); an eligible flow that keeps failing becomes \`degraded\` and is skipped in suites until edited or re-enabled.

### Usage

\`\`\`bash
# Create (starts as draft)
echo '<workflow JSON>' | canary workflow create --from-stdin --property "My App"

# Then run and approve — once approved and passing, it's eligible for suite runs
canary workflow run <workflowId> --stream
canary workflow approve <workflowId> --yes

# From file
canary workflow create --from-file workflow.json
\`\`\`

### Example workflow JSON

\`\`\`json
{
  "name": "Verify user can create an invoice",
  "description": "Tests the invoice creation flow end-to-end",
  "nodes": [
    {
      "nodeType": "login",
      "configJson": {
        "credentialId": "4f6db838-16be-49e8-8887-8d0be1c83a87",
        "credentialName": "QA User"
      }
    },
    {
      "nodeType": "navigate",
      "configJson": { "pageUrl": "/invoices", "pageTitle": "Invoices" }
    },
    {
      "nodeType": "action",
      "configJson": {
        "customActionTitle": "Create a new invoice",
        "actionDescription": "Fill out the new invoice form and save",
        "playbookSteps": [
          { "order": 1, "action": "click", "target": "New Invoice button" },
          { "order": 2, "action": "fill", "target": "Client name field", "params": { "value": "QA Test $FIRST_NAME()" } },
          { "order": 3, "action": "fill", "target": "Amount field", "params": { "value": "$RANDOM_INT(100,999)" } },
          { "order": 4, "action": "click", "target": "Save button" }
        ],
        "inlineAssertions": [
          {
            "condition": "Success message 'Invoice created' is visible and the new invoice appears in the list",
            "severity": "fail"
          }
        ]
      }
    }
  ]
}
\`\`\`

### Updating existing workflows

Use \`canary workflow sync\` to replace a workflow's nodes. It accepts the same
simple format as create — no edges, positions, or IDs needed:

\`\`\`bash
# From stdin
echo '{"nodes": [...]}' | canary workflow sync <workflowId> --from-stdin

# From file
canary workflow sync <workflowId> --from-file updated.json

# Create a proposal for review instead of applying directly
canary workflow sync <workflowId> --from-file updated.json --propose
\`\`\`

When using \`--propose\` on an existing workflow, fetch the current state first
and reuse each node's \`id\` for steps you keep — only omit \`id\` for genuinely
new steps. This is the best way to ensure the diff stays accurate so reviewers
see only the fields that actually changed:

\`\`\`bash
# Round-trip pattern for proposals
canary workflow get <workflowId> --format json > current.json
# edit current.json: change configJsons, keep ids you're not replacing
canary workflow sync <workflowId> --from-file current.json --propose
\`\`\`

### Generating API sequences

Convert a browser-based workflow to a fast API sequence using its latest successful run:

\`\`\`bash
# Generate from the workflow's latest run
canary workflow generate-api-sequence <workflowId>

# With additional guidance
canary workflow generate-api-sequence <workflowId> --guidance "Focus on the create endpoint"

# JSON output
canary workflow generate-api-sequence <workflowId> --json
\`\`\`

### Reference

Run \`canary workflow guide\` for the full authoring reference:

\`\`\`bash
canary workflow guide                  # All sections
canary workflow guide node-types       # Available node types
canary workflow guide macros           # Dynamic test data ($EMAIL, $RANDOM_STRING, etc.)
canary workflow guide playbook         # Playbook steps and caching
canary workflow guide seed-workflows   # Seed workflow patterns
canary workflow guide assertions       # Inline and standalone assertions
\`\`\`
`;
